import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, X } from 'react-feather';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Loader from '../../components/common/Loader';
import api from '../../services/api';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const verifyToken = async () => {
      try {
        // Get token from URL query parameters
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        
        if (!token) {
          setVerifying(false);
          setError('Verification token is missing');
          return;
        }
        
        // Make API request to verify the token
        await api.get(`/auth/verify?token=${token}`);
        
        // If successful, update state
        setSuccess(true);
        localStorage.removeItem('pendingVerificationEmail');
      } catch (err: any) {
        console.error('Verification failed:', err);
        setError(err.response?.data?.message || 'Verification failed');
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [location]);
  
  const goToLogin = () => {
    navigate('/login', { replace: true });
  };
  
  if (verifying) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gray-100">
        <Loader size="lg" />
        <p className="mt-4 text-gray-600">Verifying your email...</p>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          {success ? (
            <CheckCircle className="h-12 w-12 text-green-500" />
          ) : (
            <X className="h-12 w-12 text-red-500" />
          )}
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {success ? 'Email Verified!' : 'Verification Failed'}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          {success ? (
            <div className="px-6 py-8 text-center">
              <p className="text-lg text-gray-700 mb-6">
                Your email has been successfully verified. You can now log in to your account.
              </p>
              <Button
                onClick={goToLogin}
                fullWidth
              >
                Go to Login
              </Button>
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <p className="text-lg text-red-600 mb-2">
                {error || 'Verification failed'}
              </p>
              <p className="text-gray-600 mb-6">
                The verification link may have expired. Please try requesting a new verification email.
              </p>
              <Button
                onClick={() => navigate('/verification-required')}
                variant="outline"
                className="mb-3"
                fullWidth
              >
                Request New Verification
              </Button>
              <Button
                onClick={goToLogin}
                fullWidth
              >
                Go to Login
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;